import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatInr, paise } from '@bits/domain';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

export function TransactionDetailSheet({
  transactionId,
  onClose,
  accountName,
  projectLabel,
  costCenterLabel,
}: {
  transactionId: string | null;
  onClose: () => void;
  accountName: (id: string) => string;
  projectLabel: (id: string) => string;
  costCenterLabel: (id: string) => string;
}): JSX.Element {
  const detailQuery = useQuery({
    queryKey: ['transaction', transactionId],
    queryFn: () => api.getTransaction(transactionId!),
    enabled: transactionId !== null,
  });
  const t = detailQuery.data;

  return (
    <Sheet open={transactionId !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t ? formatInr(paise(t.amountPaise)) : 'Transaction'}</SheetTitle>
          <SheetDescription>
            {t ? `${t.direction === 'in' ? 'In' : 'Out'} · ${t.businessDate} · ${accountName(t.accountId)}` : ''}
          </SheetDescription>
        </SheetHeader>
        {detailQuery.isPending && transactionId ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
        ) : detailQuery.isError ? (
          <p className="text-sm text-[hsl(var(--destructive))]">{toMessage(detailQuery.error)}</p>
        ) : t ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-4 rounded-xl bg-[hsl(var(--muted))]/50 p-4 text-sm">
            <Row label="Status">
              <Badge variant={t.status === 'reversed' ? 'destructive' : 'muted'}>{t.status}</Badge>
            </Row>
            <Row label="Category">{t.categoryName ?? '—'}</Row>
            <Row label="Description" wide>
              {t.description}
            </Row>
            <Row label="Mapped to">
              {t.projectId
                ? `Project · ${projectLabel(t.projectId)}`
                : t.costCenterId
                  ? `Cost center · ${costCenterLabel(t.costCenterId)}`
                  : 'Unmapped'}
            </Row>
            <Row label="GST">
              {t.gstRelevant ? 'Relevant' : 'Not relevant'}
              {t.gstReasonCode ? ` · ${t.gstReasonCode}` : ''}
            </Row>
            <Row label="Document">
              {t.documentId ? (
                <Link
                  to="/billing/documents/$documentId"
                  params={{ documentId: t.documentId }}
                  className="text-[hsl(var(--brand))] hover:underline"
                >
                  Open document
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Transfer group" mono>
              {t.transferGroupId ?? '—'}
            </Row>
            <Row label="Reversal of" mono>
              {t.reversalOfId ?? '—'}
            </Row>
            <Row label="Recorded">{new Date(t.createdAt).toLocaleString()}</Row>
            <Row label="Source" mono>
              {t.source}
            </Row>
            <Row label="Transaction ID" mono wide>
              {t.id}
            </Row>
          </dl>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function Row({
  label,
  mono,
  wide,
  children,
}: {
  label: string;
  mono?: boolean;
  wide?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={wide ? 'col-span-2' : undefined}>
      <dt className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</dt>
      <dd className={`mt-1 break-all ${mono ? 'font-mono text-xs' : ''}`}>{children}</dd>
    </div>
  );
}
